/**
 * Phase 5.9.15 — Dispatcher Inbox (support tickets).
 *
 * Lists tickets created via the public /contact form. Lets the operator:
 *   - Filter by status (all / open / in_progress / resolved)
 *   - Tap "WORKING" / "RESOLVED" to advance status
 *   - Add internal notes (only visible to dispatchers)
 *   - Tap-to-call / tap-to-email the customer
 *
 * Regional dispatchers see only their region; SA sees all (the backend
 * enforces the scope, we just render whatever comes back).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Inbox, MessageSquare, Phone, Mail, CheckCircle2, Clock, RefreshCcw, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const STATUS_TABS = [
  { key: 'open', label: 'OPEN', color: '#FF9F0A' },
  { key: 'in_progress', label: 'WORKING', color: '#00F0FF' },
  { key: 'resolved', label: 'RESOLVED', color: '#39FF14' },
  { key: 'all', label: 'ALL', color: '#A3C7A8' },
];

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function InboxPanel({ isSuperAdmin, selectedRegion }) {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, resolved: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // { [ticketId]: bool }
  const [noteDrafts, setNoteDrafts] = useState({}); // { [ticketId]: text }
  // Feb 2026 — support deep-linking from the morning brief
  // (`/dispatch?tab=inbox&ticket=<id>`). When the param is present we widen
  // the status filter to 'all' so the ticket is found even if resolved,
  // then auto-expand its drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedTicketId = searchParams.get('ticket') || null;

  const regionParam = useMemo(() => {
    if (!isSuperAdmin) return '';
    return selectedRegion === 'all' ? 'all' : selectedRegion;
  }, [isSuperAdmin, selectedRegion]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('status', statusFilter);
      if (regionParam) qs.set('region', regionParam);
      qs.set('limit', '200');
      const { data } = await axios.get(`${API_URL}/api/admin/tickets?${qs.toString()}`, {
        withCredentials: true,
      });
      setTickets(data.tickets || []);
      setCounts(data.counts || { open: 0, in_progress: 0, resolved: 0, total: 0 });
    } catch (err) {
      console.error('inbox.fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, regionParam]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Feb 2026 — deep-link handler. When a `?ticket=<id>` is present:
  //   1. Force status filter to 'all' so any state (open/resolved/etc) loads
  //   2. After tickets arrive, expand the focused ticket + scroll it into view
  //   3. Clear the param so refresh / further nav doesn't re-trigger
  useEffect(() => {
    if (focusedTicketId && statusFilter !== 'all') {
      setStatusFilter('all');
    }
  }, [focusedTicketId, statusFilter]);

  useEffect(() => {
    if (!focusedTicketId || tickets.length === 0) return;
    const match = tickets.find((t) => (t._id || t.id) === focusedTicketId);
    if (!match) return;
    setExpanded((prev) => ({ ...prev, [focusedTicketId]: true }));
    // Scroll into view after the row renders (Tabs swap can swallow scroll)
    setTimeout(() => {
      const el = document.getElementById(`ticket-row-${focusedTicketId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    // Clear the param so it doesn't pin us if the operator navigates away
    const next = new URLSearchParams(searchParams);
    next.delete('ticket');
    setSearchParams(next, { replace: true });
  }, [focusedTicketId, tickets, searchParams, setSearchParams]);

  // 60s poll fallback
  useEffect(() => {
    const t = setInterval(fetchTickets, 60_000);
    return () => clearInterval(t);
  }, [fetchTickets]);

  const updateStatus = async (ticketId, status) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/tickets/${ticketId}/status`,
        { status },
        { withCredentials: true },
      );
      toast.success(`Ticket marked ${status.replace('_', ' ')}`);
      fetchTickets();
    } catch {
      toast.error('Could not update ticket status');
    }
  };

  const addNote = async (ticketId) => {
    const text = (noteDrafts[ticketId] || '').trim();
    if (!text) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/tickets/${ticketId}/notes`,
        { body: text },
        { withCredentials: true },
      );
      setNoteDrafts((d) => ({ ...d, [ticketId]: '' }));
      toast.success('Note added');
      fetchTickets();
    } catch {
      toast.error('Could not add note');
    }
  };

  return (
    <div className="space-y-4" data-testid="inbox-panel">
      {/* Status filter chips + counts */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          {STATUS_TABS.map((t) => {
            const n = t.key === 'all' ? counts.total : (counts[t.key] || 0);
            const active = statusFilter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setStatusFilter(t.key)}
                data-testid={`inbox-tab-${t.key}`}
                className={[
                  'px-3 py-1.5 border font-mono text-xs uppercase tracking-wider rounded-none transition-colors',
                  active
                    ? 'border-current bg-black/40'
                    : 'border-[#2A0F3D] hover:border-current',
                ].join(' ')}
                style={{ color: t.color }}
              >
                {t.label} <span className="ml-1 text-[#888]">({n})</span>
              </button>
            );
          })}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchTickets}
          disabled={loading}
          className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none font-mono h-8"
          data-testid="inbox-refresh"
        >
          <RefreshCcw className={`w-3 h-3 ${loading ? 'animate-spin' : ''} mr-1`} />
          REFRESH
        </Button>
      </div>

      {/* List */}
      {tickets.length === 0 ? (
        <div
          className="text-center text-[#888] py-12 font-mono text-sm border border-[#2A0F3D] bg-[#0C0612]"
          data-testid="inbox-empty"
        >
          <Inbox className="w-8 h-8 mx-auto mb-2 text-[#4B6B52]" />
          // no {statusFilter !== 'all' ? statusFilter.replace('_', ' ') : ''} tickets
        </div>
      ) : (
        <div className="space-y-2" data-testid="inbox-list">
          {tickets.map((t) => (
            <TicketRow
              key={t.id}
              ticket={t}
              expanded={!!expanded[t.id]}
              onToggle={() => setExpanded((e) => ({ ...e, [t.id]: !e[t.id] }))}
              onStatusChange={(s) => updateStatus(t.id, s)}
              noteDraft={noteDrafts[t.id] || ''}
              onNoteDraftChange={(v) => setNoteDrafts((d) => ({ ...d, [t.id]: v }))}
              onAddNote={() => addNote(t.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function statusBadge(status) {
  const map = {
    open: { color: '#FF9F0A', label: 'OPEN' },
    in_progress: { color: '#00F0FF', label: 'WORKING' },
    resolved: { color: '#39FF14', label: 'RESOLVED' },
  };
  const s = map[status] || map.open;
  return (
    <Badge
      variant="outline"
      className="text-[10px] px-1.5 py-0 rounded-none font-mono"
      style={{ color: s.color, borderColor: s.color }}
    >
      {s.label}
    </Badge>
  );
}

function TicketRow({ ticket, expanded, onToggle, onStatusChange, noteDraft, onNoteDraftChange, onAddNote }) {
  const { id, name, email, phone, subject, body, region, status, created_at, notes = [] } = ticket;
  const shortId = String(id).slice(-8).toUpperCase();

  return (
    <div
      data-testid={`inbox-ticket-${id}`}
      data-ticket-row-id={id}
      className="border border-[#2A0F3D] bg-[#0C0612] hover:border-[#39FF14] transition-colors"
      id={`ticket-row-${id}`}
    >
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-3 flex items-start justify-between gap-3 text-left"
        data-testid={`inbox-ticket-toggle-${id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[#39FF14] font-bold text-sm truncate">{name}</span>
            {statusBadge(status)}
            {region && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 rounded-none border-[#A3C7A8] text-[#A3C7A8]">
                {region.toUpperCase()}
              </Badge>
            )}
            <span className="text-[#666] text-[10px] font-mono">#{shortId}</span>
          </div>
          <div className="text-white text-sm truncate font-medium">{subject}</div>
          <div className="text-[#A3C7A8] text-xs truncate mt-0.5">{body}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[#888] text-[10px] font-mono flex items-center gap-1">
            <Clock className="w-3 h-3" /> {fmtTime(created_at)}
          </div>
          {notes.length > 0 && (
            <div className="text-[#00F0FF] text-[10px] font-mono mt-1">+{notes.length} note{notes.length === 1 ? '' : 's'}</div>
          )}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-[#2A0F3D] p-3 space-y-3" data-testid={`inbox-ticket-detail-${id}`}>
          {/* Full message */}
          <div>
            <div className="text-[#A3C7A8] text-[10px] font-mono uppercase tracking-wider mb-1">// MESSAGE</div>
            <div className="text-white text-sm whitespace-pre-wrap leading-relaxed bg-black/30 border border-[#2A0F3D] p-2 rounded">{body}</div>
          </div>

          {/* Contact quick actions */}
          <div className="flex flex-wrap gap-2">
            {phone && (
              <a
                href={`tel:${phone}`}
                data-testid={`inbox-ticket-call-${id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-[#39FF14] text-[#39FF14] text-xs font-mono uppercase rounded-none hover:bg-[rgba(57,255,20,0.08)]"
              >
                <Phone className="w-3 h-3" /> {phone}
              </a>
            )}
            <a
              href={`mailto:${email}?subject=Re:%20${encodeURIComponent(subject)}`}
              data-testid={`inbox-ticket-mail-${id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#2A0F3D] text-[#A3C7A8] text-xs font-mono uppercase rounded-none hover:border-[#39FF14] hover:text-[#39FF14]"
            >
              <Mail className="w-3 h-3" /> {email}
            </a>
          </div>

          {/* Status transitions */}
          <div className="flex flex-wrap gap-2">
            {status !== 'in_progress' && status !== 'resolved' && (
              <Button
                type="button"
                size="sm"
                onClick={() => onStatusChange('in_progress')}
                className="bg-[#00F0FF] hover:bg-[#33F4FF] text-[#050003] font-mono uppercase rounded-none h-8 text-xs"
                data-testid={`inbox-ticket-working-${id}`}
              >
                <Clock className="w-3 h-3 mr-1" /> MARK WORKING
              </Button>
            )}
            {status !== 'resolved' && (
              <Button
                type="button"
                size="sm"
                onClick={() => onStatusChange('resolved')}
                className="bg-[#39FF14] hover:bg-[#4AF626] text-[#050003] font-mono uppercase rounded-none h-8 text-xs"
                data-testid={`inbox-ticket-resolve-${id}`}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" /> RESOLVE
              </Button>
            )}
            {status === 'resolved' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onStatusChange('open')}
                className="border-[#FF9F0A] text-[#FF9F0A] font-mono uppercase rounded-none h-8 text-xs"
                data-testid={`inbox-ticket-reopen-${id}`}
              >
                REOPEN
              </Button>
            )}
          </div>

          {/* Notes */}
          {notes.length > 0 && (
            <div>
              <div className="text-[#A3C7A8] text-[10px] font-mono uppercase tracking-wider mb-1">// INTERNAL NOTES</div>
              <div className="space-y-1.5">
                {notes.map((n, i) => (
                  <div key={i} className="bg-black/30 border-l-2 border-[#00F0FF] p-2 text-xs">
                    <div className="text-[#00F0FF] font-mono text-[10px]">
                      {n.author_name} · {fmtTime(n.created_at)}
                    </div>
                    <div className="text-[#A3C7A8] mt-0.5 whitespace-pre-wrap">{n.body}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add note */}
          <div className="flex gap-2 items-start">
            <Input
              type="text"
              value={noteDraft}
              onChange={(e) => onNoteDraftChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onAddNote(); }}
              placeholder="// add an internal note..."
              maxLength={2000}
              className="flex-1 bg-black border-[#2A0F3D] text-white font-mono text-xs rounded-none h-9"
              data-testid={`inbox-ticket-note-input-${id}`}
            />
            <Button
              type="button"
              size="sm"
              onClick={onAddNote}
              disabled={!noteDraft.trim()}
              className="bg-[#00F0FF] hover:bg-[#33F4FF] text-[#050003] font-mono uppercase rounded-none h-9 px-3 text-xs"
              data-testid={`inbox-ticket-note-submit-${id}`}
            >
              <Send className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
